import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { EASE_EXPRESSIVE } from "@/lib/motion";

const EVENT_LINKS = [
  { label: "Luxury Weddings", to: "/event-types/luxury-weddings" },
  { label: "Corporate Events", to: "/event-types/corporate-events" },
  { label: "Embassy Receptions", to: "/event-types/embassies" },
  { label: "Museums", to: "/event-types/museums" },
  { label: "Hotels", to: "/event-types/hotels" },
  { label: "Private Estates", to: "/event-types/private-estates" },
  { label: "Brand Activations", to: "/event-types/brand-activations" },
];

const NAV = [
  { label: "Experience", to: "/experience" },
  { label: "Event Types", to: "/event-types", dropdown: EVENT_LINKS },
  { label: "Gallery", to: "/gallery" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Journal", to: "/journal" },
  { label: "About", to: "/about" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      setScrolled(y > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setEventsOpen(false);
  }, [location.pathname]);

  const overHero = location.pathname === "/";
  const solid = scrolled || !overHero;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          solid ? "bg-aria-ivory/95 backdrop-blur-md border-b border-aria-travertine py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="aria-container flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid="logo-link"
            className="shrink-0"
          >
            <img
              src={solid ? "/aria-logo-dark.png" : "/aria-logo-light.png"}
              alt="ARIA — Cocktails Experience"
              className="h-9 w-auto md:h-11"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV.map((item) => (
              <div key={item.to} className="relative group">
                <Link
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-flex items-center gap-1 whitespace-nowrap text-[0.7rem] uppercase tracking-[0.16em] font-medium transition-colors ${
                    solid ? "text-aria-ink/80 hover:text-aria-ink" : "text-aria-ivory/90 hover:text-aria-ivory"
                  }`}
                >
                  {item.label}
                  {item.dropdown && <ChevronDown className="h-3 w-3 opacity-60" />}
                </Link>
                {item.dropdown && (
                  <div className="invisible absolute left-1/2 top-full z-50 mt-4 w-64 -translate-x-1/2 translate-y-2 border border-aria-travertine bg-aria-ivory p-2 opacity-0 shadow-[0_20px_60px_-30px_rgba(28,27,26,0.4)] transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {item.dropdown.map((d) => (
                      <Link
                        key={d.to}
                        to={d.to}
                        className="block px-4 py-2.5 font-display text-lg text-aria-ink/80 transition-colors hover:bg-aria-sand hover:text-aria-ink"
                      >
                        {d.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/book")}
              data-testid="header-book-btn"
              className={`hidden md:inline-flex items-center px-6 py-3 text-[0.7rem] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${
                solid
                  ? "bg-aria-ink text-aria-ivory hover:bg-aria-cocoa"
                  : "border border-aria-ivory text-aria-ivory hover:bg-aria-ivory hover:text-aria-ink"
              }`}
            >
              Book ARIA
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
              className={`lg:hidden ${solid ? "text-aria-ink" : "text-aria-ivory"}`}
            >
              <Menu className="h-7 w-7" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-aria-ivory"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="aria-container flex items-center justify-between py-6">
              <span className="font-display text-3xl text-aria-ink"><img src="/aria-logo-dark.png" alt="ARIA" className="h-8 w-auto" /></span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X className="h-7 w-7 text-aria-ink" />
              </button>
            </div>
            <nav className="aria-container mt-6 flex flex-col gap-1">
              {NAV.filter((n) => !n.dropdown).map((item, i) => (
                <MobileLink key={item.to} to={item.to} i={i}>{item.label}</MobileLink>
              ))}
              <div className="mt-2">
                <button
                  onClick={() => setEventsOpen((v) => !v)}
                  className="flex w-full items-center justify-between font-display text-4xl text-aria-ink py-2"
                >
                  Event Types <ChevronDown className={`h-6 w-6 transition-transform ${eventsOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {eventsOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden pl-4"
                    >
                      {EVENT_LINKS.map((d) => (
                        <Link key={d.to} to={d.to} className="block py-2 font-body text-base uppercase tracking-[0.18em] text-aria-muted">
                          {d.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <Link
                to="/book"
                data-testid="mobile-book-btn"
                className="mt-8 inline-flex w-full items-center justify-center bg-aria-ink px-6 py-5 text-[0.72rem] uppercase tracking-[0.24em] text-aria-ivory"
              >
                Book ARIA
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MobileLink({ to, i, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + i * 0.06, ease: EASE_EXPRESSIVE, duration: 0.6 }}
    >
      <Link to={to} className="block font-display text-4xl text-aria-ink py-2">{children}</Link>
    </motion.div>
  );
}
